import { useTranslation } from 'react-i18next';
import { useUnifiedDictionary } from '../hooks/useUnifiedDictionary';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  MessageSquare,
  Eye,
  X,
  RotateCcw,
  Trash2,
  Target,
  Volume2,
  Brain,
  Edit,
  Share2,
  ChevronRight,
  Contrast,
  Search,
  Hash,
  Sparkles,
  Check,
  Plus,
  Minus,
  Type,
  Bold,
  Italic,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Loader2,
  Globe,
  Square,
  Play,
  Pause,
  Languages,
  Settings,
  ZoomIn,
  ZoomOut,
  Moon,
  Sun,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  ChevronDown,
  Gauge,
  Headphones,
  AlertTriangle,
  Disc,
} from "lucide-react";
import QuoteCardModal, { ShareQuoteData } from "../components/QuoteCardModal";
import { storageService } from "../services/storageService";
import { fileStorageService } from "../services/fileStorageService";
import { translationService } from "../services/translationService";
import { textToSpeechService } from "../services/textToSpeechService";
import { Book, Annotation } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { supabase } from "../services/supabaseClient";
import * as pdfjsLib from "pdfjs-dist";

// Define the workerSrc for PDF.js to avoid local build issues
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

import { ocrCacheService } from "../services/ocrCacheService";
import OcrWorker from "../workers/ocr.worker?worker";
import { flashcardManager } from "../services/flashcardManager";
import {
  notesDeckManager,
  normalizeLangCode,
} from "../services/notesDeckManager";
import { useData } from "../contexts/DataContext";
import { useStreakCounter } from "../hooks/useStreakCounter";
import { activeStudyTracker } from '../services/activeStudyTrackerService';
import { AppBackButton } from '../components/AppBackButton';

const TTS_OPTIONS = [
  { name: "Português (BRA)", locale: "pt-BR" },
  { name: "Inglês (EUA)", locale: "en-US" },
  { name: "Inglês (GBR)", locale: "en-GB" },
  { name: "Francês (FRA)", locale: "fr-FR" },
  { name: "Espanhol (ESP)", locale: "es-ES" },
  { name: "Italiano (ITA)", locale: "it-IT" },
  { name: "Alemão (DEU)", locale: "de-DE" },
  { name: "Japonês (JPN)", locale: "ja-JP" },
  { name: "Chinês (CHN)", locale: "zh-CN" },
  { name: "Russo (RUS)", locale: "ru-RU" },
  { name: "Árabe (SAU)", locale: "ar-SA" },
];

const READER_FONTS = [
  { name: "Georgia", family: "Georgia, serif" },
  {
    name: "Palatino",
    family: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
  },
  { name: "Times New Roman", family: '"Times New Roman", Times, serif' },
  { name: "Arial", family: "Arial, Helvetica, sans-serif" },
  { name: "System Default", family: "inherit" },
];

export default function ReaderPage({
  fileUrl: propFileUrl,
}: {
  fileUrl: string;
}) {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleAppOnlyTheme } = useTheme();
  const { user } = useAuth();
  const { profile } = useData();
  useStreakCounter(true);

  // States - Data & Loading
  const [loading, setLoading] = useState(true);
  const [bookData, setBookData] = useState<Book | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isReadyToSave, setIsReadyToSave] = useState(false);
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);

  // Active study tracker initialization & unmount flush for PDF reader
  useEffect(() => {
    const rawLang = bookData?.language || profile?.learning_language;
    const clean = rawLang ? rawLang.split('-')[0].toLowerCase() : null;

    if (user?.id && clean && clean !== 'all') {
      activeStudyTracker.setUserContext(user.id, clean);
    }
    return () => {
      activeStudyTracker.flush();
    };
  }, [user?.id, bookData?.language, profile?.learning_language]);

  // States - OCR
  const [tesseractReady, setTesseractReady] = useState(false);
  const [ocrStatus, setOcrStatus] = useState<string>("");
  const [ocrWords, setOcrWords] = useState<any[]>([]);
  const [ocrViewport, setOcrViewport] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // States - UI Visibility
  const [isUIVisible, setIsUIVisible] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [eyePosition, setEyePosition] = useState({ x: 24, y: 110 });
  const [isDraggingEye, setIsDraggingEye] = useState(false);
  const eyeRef = useRef<HTMLButtonElement>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const lastSavedPageRef = useRef<number | null>(null);

  const [isReadingSettingsModalOpen, setIsReadingSettingsModalOpen] =
    useState(false);
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);

  // States for Theme/Texture
  const [activeTheme, setActiveTheme] = useState<'light' | 'dark' | null>(() => {
    const saved = localStorage.getItem('readerPdfTheme');
    if (saved === 'light' || saved === 'dark') return saved;
    const savedTexture = localStorage.getItem('readerPdfTexture');
    if (savedTexture) return null;
    return isDarkMode ? 'dark' : 'light';
  });
  const [activeTexture, setActiveTexture] = useState<string | null>(() => {
    return localStorage.getItem('readerPdfTexture') || null;
  });

  // Derived styling for the reader
  const isDarkBase = activeTheme === 'dark' || activeTexture === '3.jpg';
  const iframeFilter = isDarkBase
    ? 'invert(1) hue-rotate(180deg) brightness(0.95) contrast(0.9)'
    : 'none';
  const iframeBlendMode = activeTexture ? (activeTexture === '3.jpg' ? 'screen' : 'multiply') : 'normal';

  // States - TTS & Player
  const [pageReadingStatus, setPageReadingStatus] = useState(
    textToSpeechService.getStatus(),
  );
  const [readerTtsLocale, setReaderTtsLocale] = useState(
    () => localStorage.getItem(`bookTtsLang_${id}`) || "pt-BR",
  );
  const [isReaderLangModalOpen, setIsReaderLangModalOpen] = useState(false);
  const [ttsRate, setTtsRate] = useState(1.0);

  // States - Modals & Extras
  const [isJumpModalOpen, setIsJumpModalOpen] = useState(false);
  const [jumpPageInput, setJumpPageInput] = useState("");
  const [jumpError, setJumpError] = useState<string | null>(null);

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const unifiedDict = useUnifiedDictionary();
  const [noteUpdateToggle, setNoteUpdateToggle] = useState(0);

  // Reading Settings State (For Parity with HTML Reader)
  const [readerFontSize, setReaderFontSize] = useState(() => Number(localStorage.getItem('readerPdfFontSize')) || 18);
  const [readerTextAlign, setReaderTextAlign] = useState(() => localStorage.getItem('readerPdfTextAlign') || 'left');
  const [readerMarginSize, setReaderMarginSize] = useState(() => localStorage.getItem('readerPdfMarginSize') || 'medium');
  const [readerFontFamily, setReaderFontFamily] = useState(() => localStorage.getItem('readerPdfFontFamily') || 'System Default');
  const [readerIsBold, setReaderIsBold] = useState(() => localStorage.getItem('readerPdfIsBold') === 'true');
  const [readerIsItalic, setReaderIsItalic] = useState(() => localStorage.getItem('readerPdfIsItalic') === 'true');

  useEffect(() => {
    localStorage.setItem('readerPdfFontSize', readerFontSize.toString());
  }, [readerFontSize]);

  useEffect(() => {
    localStorage.setItem('readerPdfTextAlign', readerTextAlign);
  }, [readerTextAlign]);

  useEffect(() => {
    localStorage.setItem('readerPdfMarginSize', readerMarginSize);
  }, [readerMarginSize]);

  useEffect(() => {
    localStorage.setItem('readerPdfFontFamily', readerFontFamily);
  }, [readerFontFamily]);

  useEffect(() => {
    localStorage.setItem('readerPdfIsBold', readerIsBold.toString());
  }, [readerIsBold]);

  useEffect(() => {
    localStorage.setItem('readerPdfIsItalic', readerIsItalic.toString());
  }, [readerIsItalic]);

  // Load PDF Content
  useEffect(() => {
    let currentBlobUrl = "";
    const loadContent = async () => {
      setLoading(true);
      try {
        const books = storageService.getBooks();
        const found = books.find((b) => b.id === id || b.custom_id === id);
        if (found) setBookData(found);

        const notes = storageService.getAnnotations(id || "");
        setAnnotations(notes);

        const isPublicFile =
          propFileUrl?.startsWith("/") && !propFileUrl.includes("indexeddb");
        let finalBaseUrl = "";

        if (isPublicFile) {
          finalBaseUrl = propFileUrl;
        } else {
          const blob = await fileStorageService.getFile(id || "");
          if (blob) {
            currentBlobUrl = URL.createObjectURL(blob);
            finalBaseUrl = currentBlobUrl;
          } else {
            const cleanPath = propFileUrl?.replace(/^\//, "") || id;
            finalBaseUrl = cleanPath?.startsWith("http")
              ? cleanPath
              : `/${cleanPath}`;
          }
        }

        const best = await storageService.getBestProgress(user?.id, id || "");
        const initialPage = best?.page || 1;
        setCurrentPage(initialPage);
        setPdfUrl(prev => {
          const newPage = initialPage;
          let url = `${finalBaseUrl}#page=${newPage}&view=Fit&toolbar=0&navpanes=0&scrollbar=0`;
          if (isDarkBase) {
            url += "&viewer=dark";
          }
          return url;
        });
        setIsReadyToSave(true);

        // Load PDF Document for OCR and getting total pages
        const loadingTask = pdfjsLib.getDocument(finalBaseUrl);
        const doc = await loadingTask.promise;
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
      } catch (err) {
        console.error("Erro ao carregar PDF:", err);
      } finally {
        setLoading(false);
      }
    };
    if (user !== undefined) loadContent();
    return () => {
      if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
    };
  }, [id, propFileUrl, user]);

  // Sync Progress
  useEffect(() => {
    if (!bookData?.id || !currentPage || !isReadyToSave) return;
    if (lastSavedPageRef.current === currentPage) return;

    // GUARD CLAUSE: Ensure user variables are fully loaded and valid
    if (!user || !user.id || user.id === 'undefined' || user.id === 'null') {
      return;
    }

    const safeUserId = user.isGuest ? undefined : user.id;

    lastSavedPageRef.current = currentPage;
    storageService.saveBookProgress(
      safeUserId,
      bookData.id,
      currentPage,
      totalPages || 0,
    );
  }, [currentPage, bookData?.id, user?.id, user?.isGuest, totalPages, isReadyToSave]);

  // Sync pending progress on component unmount
  useEffect(() => {
    return () => {
      storageService.syncPendingProgress();
    };
  }, []);

  // Pagination Logic
  const jumpToPage = (num: number) => {
    const target = Math.max(1, Math.min(num, totalPages || 1));
    setCurrentPage(target);
    const baseUrl = pdfUrl.split("#")[0];
    setPdfUrl(
      `${baseUrl}#page=${target}&view=Fit&toolbar=0&navpanes=0&scrollbar=0`,
    );
  };

  const handleJumpSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const pageNum = parseInt(jumpPageInput, 10);
    if (isNaN(pageNum) || pageNum < 1) return;
    jumpToPage(pageNum);
    setIsJumpModalOpen(false);
    setJumpPageInput("");
  };

  const isDraggingEyeRef = useRef(false);
  const isPotentiallyDraggingRef = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });

  const handleEyeDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if ('button' in e && e.button !== 0) return;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    dragOffset.current = { x: clientX - rect.left, y: rect.bottom - clientY };
    dragStartPos.current = { x: clientX, y: clientY };
    isPotentiallyDraggingRef.current = true;
    isDraggingEyeRef.current = false;
    setIsDraggingEye(false);
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!isPotentiallyDraggingRef.current || !eyeRef.current) return;
      const clientX =
        "touches" in e
          ? (e as TouchEvent).touches[0].clientX
          : (e as MouseEvent).clientX;
      const clientY =
        "touches" in e
          ? (e as TouchEvent).touches[0].clientY
          : (e as MouseEvent).clientY;

      if (!isDraggingEyeRef.current) {
        const dx = clientX - dragStartPos.current.x;
        const dy = clientY - dragStartPos.current.y;
        if (Math.sqrt(dx*dx + dy*dy) > 5) {
          isDraggingEyeRef.current = true;
          setIsDraggingEye(true);
        } else {
          return;
        }
      }

      if (e.cancelable) {
        e.preventDefault();
      }

      const newX = Math.max(
        16,
        Math.min(window.innerWidth - 64, clientX - dragOffset.current.x),
      );
      const newY = Math.max(
        16,
        Math.min(
          window.innerHeight - 64,
          window.innerHeight - clientY - dragOffset.current.y,
        ),
      );
      eyeRef.current.style.left = `${newX}px`;
      eyeRef.current.style.bottom = `${newY}px`;
    };
    const handleEnd = () => {
      if (!isPotentiallyDraggingRef.current) return;
      isPotentiallyDraggingRef.current = false;

      if (!isDraggingEyeRef.current) return;

      setTimeout(() => {
          isDraggingEyeRef.current = false;
          setIsDraggingEye(false);
      }, 50);

      if (eyeRef.current) {
         const rect = eyeRef.current.getBoundingClientRect();
         setEyePosition({ x: rect.left, y: window.innerHeight - rect.bottom });
      }
    };
    
    window.addEventListener("mousemove", handleMove, { passive: false });
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchend", handleEnd);
    
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchend", handleEnd);
    };
  }, []);

  // OCR Processing logic (Client-Side Worker + IndexedDB)
  useEffect(() => {
    if (!pdfDoc) return;

    let isCancelled = false;
    let worker: Worker | null = null;

    const runOCR = async () => {
      const bookKey = id || "unknown_book";

      // 1. Setup exact screen layout container parameters
      try {
        const page = await pdfDoc.getPage(currentPage);
        const viewport = page.getViewport({ scale: 2.0 }); // High scale for detection accuracy
        setOcrViewport({ width: viewport.width, height: viewport.height });

        // 2. Check IndexedDB Cache
        const cached = await ocrCacheService.getCachedPage(bookKey, currentPage);
        if (cached && cached.words && cached.viewportWidth === viewport.width) {
          if (!isCancelled) {
            setOcrWords(cached.words as any[]);
            setOcrStatus(""); // Clear instantly since it's cached
          }
          return;
        }

        if (isCancelled) return;
        setOcrStatus(`Processando página ${currentPage} via hardware local...`);
        setOcrWords([]);

        // 3. Render offscreen canvas to pass to worker
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (!context) return;
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({ canvasContext: context, viewport }).promise;
        if (isCancelled) return;

        // Pass ImageBitmap or ImageData instead of data URL for immense performance gain
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

        // 4. Send to Worker
        worker = new OcrWorker();
        const lang = "por+eng";

        worker.postMessage({ imageData, lang });

        worker.onmessage = async (e) => {
          if (isCancelled) return;
          const { type, data, error } = e.data;

          if (type === 'progress') {
            // Optional progress logging
          } else if (type === 'success') {
            const extractedWords = data;
            setOcrWords(extractedWords);
            setOcrStatus("Texto selecionável ativado");
            setTimeout(() => { if (!isCancelled) setOcrStatus(""); }, 3000);

            // Save to IDB Cache
            await ocrCacheService.saveCachedPage({
              bookId: bookKey,
              pageNumber: currentPage,
              viewportWidth: viewport.width,
              viewportHeight: viewport.height,
              words: extractedWords,
              timestamp: Date.now()
            });

            if (worker) { worker.terminate(); worker = null; }
          } else if (type === 'error') {
            console.error("Worker OCR Error:", error);
            setOcrStatus("");
            if (worker) { worker.terminate(); worker = null; }
          }
        };

        worker.onerror = (err) => {
          console.error("Worker exception:", err);
          if (!isCancelled) setOcrStatus("");
          if (worker) { worker.terminate(); worker = null; }
        };

      } catch (err) {
        console.error("OCR Pipeline Error:", err);
        if (!isCancelled) setOcrStatus("");
      }
    };

    runOCR();

    return () => {
      isCancelled = true;
      if (worker) {
        worker.terminate();
      }
    };
  }, [currentPage, pdfDoc]);

  // Styles CSS (Injected to match LibriTuiReaderPage)
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .reader-root-container {
        position: fixed; inset: 0; width: 100vw; height: 100vh;
        overflow: hidden; display: flex; flex-direction: column;
      }
      .ocr-selection-layer {
        position: absolute;
        top: 0; left: 0; width: 100%; height: 100%;
        z-index: 999;
      }
      .ocr-word {
        position: absolute;
        color: transparent;
        user-select: text !important;
        -webkit-user-select: text !important;
        pointer-events: auto !important;
        z-index: 1000;
        cursor: pointer;
        line-height: 1;
        transform-origin: 0 0;
      }
      .hud-element {
        backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        border: 1px solid rgba(255, 255, 255, 0.08);
        background-color: rgba(10, 15, 25, 0.8) !important;
        color: white;
      }
      .bottom-player-card {
        position: fixed; bottom: 8rem; left: 50%; transform: translateX(-50%);
        z-index: 155; width: auto; height: 4.5rem; border-radius: 3rem;
        padding: 0 1.5rem; display: flex; align-items: center; justify-content: center; gap: 1rem;
      }
      .bottom-fixed-footer {
        position: fixed; bottom: 0; left: 0; right: 0; z-index: 160;
        height: 5.5rem; border-radius: 2rem 2rem 0 0; padding: 0 1.5rem;
        display: flex; align-items: center; justify-content: space-between; gap: 1rem;
      }
      .ui-toggle-btn {
        position: fixed; z-index: 180; width: 4.2rem; height: 4.2rem;
        border-radius: 50%; background-color: white; display: flex;
        align-items: center; justify-content: center; cursor: grab;
      }
      .dark .ui-toggle-btn { background-color: #252F3E; border: 1px solid rgba(255, 255, 255, 0.1); }
      .custom-scrollbar::-webkit-scrollbar { width: 6px; }
      .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 10px; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (loading)
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-950 relative overflow-hidden select-none">
        {/* Botão de Voltar no Topo Esquerdo */}
        <div className="absolute top-6 left-6 z-50">
          <AppBackButton fallbackTo="/library-hub" />
        </div>

        {/* Conteúdo do Carregamento com Barra de Progresso */}
        <div className="flex flex-col items-center max-w-sm px-6 text-center z-10">
          <Loader2 className="animate-spin text-primary mb-5" size={56} />
          <p className="text-slate-400 font-normal uppercase font-mono text-xs tracking-[0.14em] animate-pulse mb-4">
            Sincronizando PDF...
          </p>

          {/* Barra de Carregamento */}
          <div className="w-64 max-w-[80vw] h-2.5 bg-slate-900 rounded-full overflow-hidden border border-slate-800 p-0.5">
            <div className="h-full bg-gradient-to-r from-ok via-primary to-ok rounded-full animate-pulse w-full shadow-[0_0_12px_rgba(16,185,129,0.5)]" />
          </div>
        </div>
      </div>
    );

  return (
    <div
      className={`reader-root-container select-none ${isDarkMode ? "dark bg-slate-950" : "bg-surface"}`}
    >
      {/* HEADER (100% PARITY) */}
      <header
        className={`fixed top-0 left-0 right-0 py-6 hud-element px-6 flex items-center justify-between z-[150] transition-all duration-500 rounded-b-[2rem] border-t-0 ${isUIVisible ? "" : "opacity-0 pointer-events-none"}`}
      >
        <div className="w-12" />
        <div className="flex-1 flex flex-col items-center text-center px-4">
          <h1 className="text-xl md:text-2xl font-medium text-white tracking-[-0.03em] line-clamp-1">
            {bookData?.title}
          </h1>
          <p className="text-[11px] md:text-xs font-normal text-slate-400 uppercase font-mono tracking-[0.14em] mt-1">
            {bookData?.author ||
              bookData?.authors?.[0]?.name ||
              "Autor Desconhecido"}
          </p>
        </div>
        <div className="w-12" />
      </header>

      {/* BACK BUTTON */}
      <div className={`fixed top-6 left-6 z-[151] transition-opacity duration-300 ${isUIVisible ? "opacity-100" : "opacity-40"}`}>
        <AppBackButton fallbackTo="/library-hub" />
      </div>

      {/* OCR Status Bar */}
      {ocrStatus && ocrStatus !== "Concluído" && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 mt-20 z-[140] bg-black/60 backdrop-blur-md px-4 py-1 rounded-full border border-white/10 pointer-events-none">
          <p className="text-white text-[10px] font-normal tracking-[0.14em] uppercase font-mono animate-pulse">
            {ocrStatus}
          </p>
        </div>
      )}

      {/* MAIN PDF VIEW */}
      <main
        className="flex-1 w-full relative overflow-hidden flex items-center justify-center transition-all duration-500"
        style={{
          backgroundColor: activeTexture ? 'transparent' : (activeTheme === 'dark' ? '#0c0805' : '#F4F1EC'),
          backgroundImage: activeTexture ? `url(/textures/${activeTexture})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Container that strictly holds the iframe and OCR layer at the same aspect ratio */}
        <div className="relative w-full h-full flex items-center justify-center isolate">
          <iframe
            key={pdfUrl}
            src={pdfUrl}
            className="absolute inset-0 w-full h-full object-contain z-0 transition-all duration-500"
            scrolling="no"
            style={{
              border: "none",
              pointerEvents: "none",
              userSelect: "none",
              filter: iframeFilter,
              mixBlendMode: iframeBlendMode as any,
              outline: 'none',
              overflow: 'hidden',
              backgroundColor: 'transparent'
            }} // Prevents native zooming and scrolling
            title="Reader"
          />

          {/* Glass Layer for Selection */}
          {ocrViewport && ocrWords.length > 0 && (
            <div className="ocr-selection-layer">
              {ocrWords.map((word, i) => {
                const { bbox, text } = word;
                // convert bbox to percentages of the ocrViewport
                const left = (bbox.x0 / ocrViewport.width) * 100;
                const top = (bbox.y0 / ocrViewport.height) * 100;
                const width = ((bbox.x1 - bbox.x0) / ocrViewport.width) * 100;
                const height = ((bbox.y1 - bbox.y0) / ocrViewport.height) * 100;

                return (
                  <span
                    key={i}
                    className="ocr-word"
                    onClick={(e) => {
                      // Instantly select the word on touch/click
                      const selection = window.getSelection();
                      if (!selection) return;
                      const range = document.createRange();
                      range.selectNodeContents(e.currentTarget);
                      selection.removeAllRanges();
                      selection.addRange(range);
                    }}
                    style={{
                      left: `${left}%`,
                      top: `${top}%`,
                      width: `${width}%`,
                      height: `${height}%`,
                    }}
                    title={text}
                  >
                    {text + " "}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* PLAYER (100% PARITY) */}
      <div
        className={`bottom-player-card hud-element z-[155] ${isUIVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 scale-95 pointer-events-none"} transition-all duration-500`}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsReaderLangModalOpen(true)}
            className="w-12 h-12 flex items-center justify-center text-slate-400 hover:text-tintd-lavender transition-colors bg-white/5 rounded-2xl border border-white/5"
          >
            <Globe size={22} />
          </button>
          <button className="w-12 h-12 flex items-center justify-center text-slate-400 hover:text-bad transition-colors bg-white/5 rounded-2xl border border-white/5">
            <Square size={20} fill="currentColor" />
          </button>
          <button className="w-14 h-14 bg-surface text-ink rounded-full flex items-center justify-center hover:scale-105 transition-all">
            <Play size={28} fill="currentColor" className="ml-1" />
          </button>
        </div>
      </div>

      {/* FOOTER (100% PARITY) */}
      <div
        className={`bottom-fixed-footer hud-element shadow-[0_-20px_50px_rgba(0,0,0,0.3)] transition-all duration-300 ${isUIVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full pointer-events-none"}`}
      >
        <div className="flex flex-col flex-1 pl-1 md:pl-2 min-w-0">
          <div className="flex items-center justify-between md:justify-start gap-1 md:gap-4 mb-2">
            <button
              onClick={() => jumpToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="p-1 md:p-2 text-slate-400 hover:text-white disabled:opacity-20 transition-all active:scale-90 shrink-0"
            >
              <ChevronLeft size={24} />
            </button>
            <span className="text-[10px] md:text-sm font-semibold text-white tracking-widest whitespace-nowrap min-w-0 truncate text-center flex-1 md:flex-none">
              <span className="hidden xs:inline">PÁG </span>{currentPage} {totalPages > 1 ? `/ ${totalPages}` : ""}
            </span>
            <button
              onClick={() => jumpToPage(currentPage + 1)}
              className="p-1 text-slate-400 hover:text-white transition-all"
            >
              <ChevronRight size={24} />
            </button>
            <button
              onClick={() => setIsJumpModalOpen(true)}
              className="p-1 text-slate-400 hover:text-white transition-all"
            >
              <Hash size={20} />
            </button>
          </div>
          <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-surface transition-all duration-300 shadow-[0_0_8px_rgba(255,255,255,0.5)]"
              style={{
                width:
                  totalPages > 1
                    ? `${(currentPage / totalPages) * 100}%`
                    : "50%",
              }}
            />
          </div>
        </div>
        <div className="w-px h-10 bg-white/10 mx-2" />
        <div className="flex items-center gap-1.5 md:gap-3 pr-1 md:pr-2 shrink-0">
          <button
            onClick={() => setIsThemeModalOpen(true)}
            className={`w-10 h-10 rounded-full transition-all border ${isDarkBase ? "bg-slate-900 border-white/20" : "bg-surface border-black/[0.06]"}`}
          >
            <div
              className={`w-full h-full rounded-full flex items-center justify-center ${isDarkBase ? "text-white" : "text-slate-900"}`}
            >
              <Contrast size={18} />
            </div>
          </button>
          <button
            onClick={() => setIsReadingSettingsModalOpen(true)}
            className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center text-white hover:bg-white/20 transition-all border border-white/10 shrink-0"
          >
            <div className="flex items-baseline leading-none gap-0.5">
              <span className="text-lg font-semibold">A</span>
              <span className="text-xs font-semibold">a</span>
            </div>
          </button>
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-white/5 rounded-xl text-slate-400 hover:text-white transition-all shrink-0"
          >
            <MessageSquare size={20} />
          </button>
        </div>
      </div>

      {/* EYE BUTTON (Draggable) */}
      <button
        ref={eyeRef}
        onMouseDown={handleEyeDragStart}
        onTouchStart={handleEyeDragStart}
        onClick={() => {
          if (!isDraggingEyeRef.current) setIsUIVisible(!isUIVisible);
        }}
        className="ui-toggle-btn shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] transition-all fixed z-[180] w-14 h-14 bg-slate-900 border border-white/10 rounded-full flex items-center justify-center"
        style={{ left: `${eyePosition.x}px`, bottom: `${eyePosition.y}px`, zIndex: 180 }}
      >
        <Eye
          size={24}
          className={isUIVisible ? "text-primary" : "text-slate-400"}
        />
      </button>

      {/* SIDEBAR (Mirroring HTML Reader) */}
      {isSidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-[240] bg-black/70 backdrop-blur-[60px] animate-in fade-in duration-500"
            onClick={() => setIsSidebarOpen(false)}
          />
          <aside className="fixed right-0 top-0 bottom-0 w-[85vw] md:w-[400px] h-full border-l border-white/5 bg-slate-950/90 backdrop-blur-2xl p-6 flex flex-col gap-6 z-[250] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] animate-in slide-in-from-right duration-500">
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="absolute top-4 right-4 text-slate-400"
            >
              <X size={24} />
            </button>
            <h2 className="font-normal text-[10px] uppercase font-mono tracking-[0.14em] text-primary">
              Minhas Notas (PDF)
            </h2>
            <div className="flex-1 overflow-y-auto space-y-4 pr-1 custom-scrollbar">
              {annotations.map((a) => (
                <div
                  key={a.id}
                  className="p-6 rounded-3xl bg-white/5 border border-white/5"
                >
                  <p className="text-[14px] font-semibold leading-tight text-white mb-2">
                    {a.content.split(" -> ")[0]}
                  </p>
                  <p className="text-[13px] font-bold text-slate-400">
                    {a.content.split(" -> ")[1] || "Traduzindo..."}
                  </p>
                </div>
              ))}
              {annotations.length === 0 && (
                <div className="py-20 text-center opacity-40 text-white">
                  <MessageSquare size={48} className="mx-auto mb-4" />
                  <p>{t('reader.noNotesFound')}</p>
                </div>
              )}
            </div>
          </aside>
        </>
      )}

      {/* THEME & TEXTURE MODAL */}
      {isThemeModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="w-full sm:w-[500px] max-h-[85vh] h-[85vh] sm:h-auto bg-surface text-ink rounded-t-3xl sm:rounded-3xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-8 sm:slide-in-from-bottom-0 sm:zoom-in-95 border border-white/10 relative">
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-black/[0.06] dark:border-white/[0.06] shrink-0">
              <h3 className="text-base md:text-lg font-medium tracking-[-0.03em] flex items-center gap-2">
                <Contrast size={20} className="text-primary" />
                {t('reader.themeAndTexture', 'Tema & Textura')}
              </h3>
              <button
                onClick={() => setIsThemeModalOpen(false)}
                className="group w-9 h-9 flex items-center justify-center rounded-full text-muted hover:text-ink hover:bg-tint-stone transition-all duration-300 active:scale-90"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="p-4 md:p-6 overflow-y-auto overflow-x-hidden custom-scrollbar flex flex-col gap-8 pb-32">
              {/* Tema Section */}
              <div className="flex flex-col gap-3">
                <h4 className="text-[10px] md:text-xs font-normal text-slate-400 uppercase font-mono tracking-[0.14em]">
                  {t('reader.baseTheme', 'Tema Base')}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => {
                      setActiveTheme('light');
                      setActiveTexture(null);
                      localStorage.setItem('readerPdfTheme', 'light');
                      localStorage.removeItem('readerPdfTexture');
                    }}
                    className={`h-24 rounded-2xl border transition-all flex flex-col items-center justify-center relative overflow-hidden bg-surface text-black ${activeTheme === 'light' && !activeTexture ? 'border-primary ring-4 ring-primary/20 scale-[1.02]' : 'border-transparent hover:border-slate-300'}`}
                  >
                    <span className="font-serif italic font-bold">{t('reader.lightTheme', 'Claro')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setActiveTheme('dark');
                      setActiveTexture(null);
                      localStorage.setItem('readerPdfTheme', 'dark');
                      localStorage.removeItem('readerPdfTexture');
                    }}
                    className={`h-24 rounded-2xl border transition-all flex flex-col items-center justify-center relative overflow-hidden bg-[#252F3E] text-white ${activeTheme === 'dark' && !activeTexture ? 'border-primary ring-4 ring-primary/20 scale-[1.02]' : 'border-white/10 hover:border-white/30'}`}
                  >
                    <span className="font-serif italic font-bold">{t('reader.darkTheme', 'Escuro')}</span>
                  </button>
                </div>
              </div>

              {/* Texturas Section */}
              <div className="flex flex-col gap-4">
                <h4 className="text-[10px] md:text-xs font-normal text-slate-400 uppercase font-mono tracking-[0.14em]">
                  Textura do Livro
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {Array.from({ length: 17 }).map((_, i) => {
                    const textureFile = `${i + 1}.jpg`;
                    const isActive = activeTexture === textureFile;
                    const isDarkTexture = i + 1 === 3; // 3.jpg is dark

                    return (
                      <button
                        key={i}
                        onClick={() => {
                          setActiveTexture(textureFile);
                          setActiveTheme(null);
                          localStorage.setItem('readerPdfTexture', textureFile);
                          localStorage.removeItem('readerPdfTheme');
                        }}
                        className={`aspect-[3/4] rounded-xl border transition-all flex flex-col p-3 relative overflow-hidden group ${isActive ? 'border-primary ring-4 ring-primary/20 scale-[1.05] z-10' : 'border-white/10 hover:border-white/30 hover:scale-105'}`}
                        style={{
                          backgroundImage: `url(/textures/${textureFile})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        {/* Simulate book text inside */}
                        <div
                          className={`w-full h-full flex flex-col ${isDarkTexture ? 'text-white/80' : 'text-black/80'}`}
                          style={{ mixBlendMode: isDarkTexture ? 'screen' : 'multiply' }}
                        >
                          <span className="text-[8px] sm:text-[10px] font-serif italic mb-1 opacity-80 border-b border-current pb-0.5 whitespace-nowrap overflow-hidden text-ellipsis">{t('reader.chapter1')}</span>
                          <div className="flex-1 flex flex-col gap-[3px] mt-1 opacity-70">
                            <div className="w-full h-[2px] bg-current rounded-full" />
                            <div className="w-5/6 h-[2px] bg-current rounded-full" />
                            <div className="w-11/12 h-[2px] bg-current rounded-full" />
                            <div className="w-4/5 h-[2px] bg-current rounded-full" />
                          </div>
                        </div>
                        {isActive && (
                          <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-ink text-surface rounded-full flex items-center justify-center">
                            <Check size={10} strokeWidth={4} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Bottom Button Fixed in Modal */}
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-[#0c0805] via-[#0c0805]/95 to-transparent pointer-events-none flex justify-center pb-6 md:pb-8">
              <button
                onClick={() => setIsThemeModalOpen(false)}
                className="w-[90%] sm:w-auto px-10 py-4 bg-primary text-slate-900 rounded-2xl font-medium text-[13px] hover:bg-primary/90 active:scale-95 transition-all pointer-events-auto flex items-center justify-center gap-2"
              >
                <Check size={18} /> {t('reader.confirmBtn', 'Confirmar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* JUMP TO PAGE MODAL */}
      {isJumpModalOpen && (
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-xl"
          onClick={() => setIsJumpModalOpen(false)}
        >
          <div
            className="bg-slate-900 p-8 rounded-[28px] border border-white/10 w-full max-w-sm animate-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white font-normal text-xs uppercase font-mono tracking-[0.14em] mb-6">
              Ir para Página
            </h3>
            <form onSubmit={handleJumpSubmit} className="space-y-4">
              <input
                type="number"
                value={jumpPageInput}
                onChange={(e) => setJumpPageInput(e.target.value)}
                placeholder="{t('reader.pageNumber')}"
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:border-primary transition-all"
                autoFocus
              />
              <button
                type="submit"
                className="w-full bg-primary text-slate-900 py-4 rounded-2xl font-semibold active:scale-95 transition-all"
              >
                Navegar
              </button>
            </form>
          </div>
        </div>
      )}

      {/* READING SETTINGS MODAL */}
      {
        isReadingSettingsModalOpen && (
          <div className={`fixed inset-0 z-[1001] flex items-end justify-center pb-[110px] px-6 pt-6 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-200 ${isUIVisible ? '' : 'ui-invisible'}`} onClick={() => setIsReadingSettingsModalOpen(false)}>
            <div className="bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[85vw] max-w-lg max-h-[70vh] flex flex-col overflow-hidden animate-in slide-in-from-bottom-10 zoom-in-95 duration-300 relative" onClick={(e) => e.stopPropagation()}>

              {/* Header - Fixed */}
              <div className="p-8 md:p-10 pb-4 flex justify-between items-start shrink-0">
                <div className="space-y-1">
                  <h3 className="text-xl font-medium text-ink tracking-[-0.02em]">{t('reader.readingSettings')}</h3>
                  <p className="text-[10px] font-normal text-slate-500 uppercase font-mono tracking-[0.14em]">{t('reader.personalizeExp')}</p>
                </div>
                <button
                  onClick={() => setIsReadingSettingsModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto px-8 md:px-10 pb-10 space-y-8 custom-scrollbar">

                {/* Font Size */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-muted">
                    <Type size={16} />
                    <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em]">{t('reader.fontSize')}</span>
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 p-2 rounded-[24px] border border-black/[0.06] dark:border-white/[0.06]">
                    <button
                      onClick={() => setReaderFontSize(prev => Math.max(12, prev - 2))}
                      className="w-14 h-14 flex items-center justify-center bg-surface dark:bg-slate-700 rounded-2xl active:scale-90 transition-all text-slate-600 dark:text-ink"
                    >
                      <Minus size={24} />
                    </button>
                    <span className="text-xl font-medium text-ink tracking-[-0.02em] tracking-[-0.04em]">{readerFontSize}px</span>
                    <button
                      onClick={() => setReaderFontSize(prev => Math.min(32, prev + 2))}
                      className="w-14 h-14 flex items-center justify-center bg-surface dark:bg-slate-700 rounded-2xl active:scale-90 transition-all text-slate-600 dark:text-ink"
                    >
                      <Plus size={24} />
                    </button>
                  </div>
                </div>

                {/* Text Style: Bold / Italic */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-muted">
                    <Sparkles size={16} />
                    <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em]">{t('reader.textStyle')}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setReaderIsBold(!readerIsBold)}
                      className={`h-16 flex items-center justify-center gap-3 rounded-2xl border transition-all ${readerIsBold
                        ? 'border-transparent bg-ink text-surface'
                        : 'border-transparent bg-tint-stone/60 text-muted hover:text-ink hover:bg-tint-stone'
                        }`}
                    >
                      <Bold size={20} />
                      <span className="text-[11px] font-normal uppercase font-mono tracking-[0.14em]">Negrito</span>
                    </button>
                    <button
                      onClick={() => setReaderIsItalic(!readerIsItalic)}
                      className={`h-16 flex items-center justify-center gap-3 rounded-2xl border transition-all ${readerIsItalic
                        ? 'border-transparent bg-ink text-surface'
                        : 'border-transparent bg-tint-stone/60 text-muted hover:text-ink hover:bg-tint-stone'
                        }`}
                    >
                      <Italic size={20} />
                      <span className="text-[11px] font-normal uppercase font-mono tracking-[0.14em]">{t('reader.italic')}</span>
                    </button>
                  </div>
                </div>

                {/* Text Alignment */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-muted">
                    <AlignLeft size={16} />
                    <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em]">Alinhamento</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { id: 'left', icon: AlignLeft },
                      { id: 'center', icon: AlignCenter },
                      { id: 'right', icon: AlignRight },
                      { id: 'justify', icon: AlignJustify },
                    ].map((align) => (
                      <button
                        key={align.id}
                        onClick={() => setReaderTextAlign(align.id)}
                        className={`h-16 flex items-center justify-center rounded-2xl border transition-all ${readerTextAlign === align.id
                          ? 'border-transparent bg-ink text-surface'
                          : 'border-transparent bg-tint-stone/60 text-muted hover:text-ink hover:bg-tint-stone'
                          }`}
                      >
                        <align.icon size={24} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Margins / Respiro */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-muted">
                    <Square size={16} />
                    <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em]">{t('reader.marginsHoriz')}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'narrow', label: 'Estreito' },
                      { id: 'medium', label: 'Médio' },
                      { id: 'wide', label: 'Largo' },
                    ].map((margin) => (
                      <button
                        key={margin.id}
                        onClick={() => setReaderMarginSize(margin.id)}
                        className={`h-12 flex items-center justify-center rounded-2xl text-[10px] font-normal uppercase font-mono tracking-[0.14em] border transition-all ${readerMarginSize === margin.id
                          ? 'border-transparent bg-ink text-surface'
                          : 'border-transparent bg-tint-stone/60 text-muted hover:text-ink hover:bg-tint-stone'
                          }`}
                      >
                        {margin.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Family Selection */}
                <div className="space-y-4 pb-4">
                  <div className="flex items-center gap-2 text-muted">
                    <Type size={16} />
                    <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em]">{t('reader.preferredFont')}</span>
                  </div>
                  <div className="space-y-2">
                    {READER_FONTS.map((font) => (
                      <button
                        key={font.name}
                        onClick={() => setReaderFontFamily(font.name)}
                        className={`w-full flex items-center justify-between px-6 py-5 rounded-3xl border transition-all ${readerFontFamily === font.name
                          ? 'border-transparent bg-ink text-surface'
                          : 'border-transparent bg-tint-stone/60 text-ink hover:bg-tint-stone'
                          }`}
                        style={{ fontFamily: font.family }}
                      >
                        <span className="text-sm font-bold">{font.name}</span>
                        {readerFontFamily === font.name && <Check size={18} />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* TTS LANG MODAL */}
      {isReaderLangModalOpen && (
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-xl"
          onClick={() => setIsReaderLangModalOpen(false)}
        >
          <div
            className="bg-slate-900 p-8 rounded-[28px] border border-white/10 w-full max-w-sm animate-in zoom-in-95 overflow-y-auto max-h-[80vh] custom-scrollbar"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white font-normal text-xs uppercase font-mono tracking-[0.14em] mb-6">
              Idioma do Áudio
            </h3>
            <div className="grid grid-cols-1 gap-2">
              {TTS_OPTIONS.map((opt) => (
                <button
                  key={opt.locale}
                  onClick={() => {
                    setReaderTtsLocale(opt.locale);
                    localStorage.setItem(`bookTtsLang_${id}`, opt.locale);
                    setIsReaderLangModalOpen(false);
                  }}
                  className={`p-4 rounded-2xl text-left transition-all ${readerTtsLocale === opt.locale ? "bg-ink text-surface" : "text-slate-400 hover:bg-white/5"}`}
                >
                  <span className="font-bold">{opt.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
